import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  Output,
  EventEmitter
} from '@angular/core';
import { Router } from '@angular/router';
import * as Chart from 'chart.js';
import { ToastrService } from 'ngx-toastr';
import {
  StorageStats,
  StorageKpiCard,
  StoreQuantityPoint,
  TransactionTrendPoint,
  StoreStockSegment,
  StorageTransaction,
  StorageQuickAction,
  StorageDashboardData
} from 'src/app/models/Storage/storage-dashboard';
import { StorageDashboardRepository } from 'src/app/repositories/storage/dashboard-repository/StorageDashboardRepository';

@Component({
  selector: 'app-storage-dashboard',
  templateUrl: './storage-dashboard.component.html',
  styleUrls: ['./storage-dashboard.component.scss']
})
export class StorageDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @Output() actionTriggered = new EventEmitter<string>();

  stats: StorageStats = {
    TotalStores: 0,
    TotalQuantityIn: 0,
    TotalQuantityOut: 0,
    CurrentStock: 0,
    InTransactions: 0,
    OutTransactions: 0,
    TotalTransactions: 0,
    LowStockStores: 0
  };

  kpiCards: StorageKpiCard[] = [
    { label: 'Total Stores', valueKey: 'TotalStores', icon: 'fa-database', tone: 'primary' },
    { label: 'BINS In', valueKey: 'TotalQuantityIn', icon: 'fa-arrow-down', tone: 'success' },
    { label: 'BINS Out', valueKey: 'TotalQuantityOut', icon: 'fa-arrow-up', tone: 'danger' },
    { label: 'Current Stock', valueKey: 'CurrentStock', icon: 'fa-cubes', tone: 'info' },
    { label: 'In Transactions', valueKey: 'InTransactions', icon: 'fa-sign-in', tone: 'success' },
    { label: 'Out Transactions', valueKey: 'OutTransactions', icon: 'fa-sign-out', tone: 'warning' },
    { label: 'Total Transactions', valueKey: 'TotalTransactions', icon: 'fa-exchange', tone: 'purple' },
    { label: 'Low Stock Stores', valueKey: 'LowStockStores', icon: 'fa-exclamation-triangle', tone: 'danger' }
  ];

  quickActions: StorageQuickAction[] = [
    { label: 'Add Store', icon: 'fa-database', tone: 'primary', action: 'add-store' },
    { label: 'Stock In', icon: 'fa-arrow-down', tone: 'success', action: 'stock-in' },
    { label: 'Stock Out', icon: 'fa-arrow-up', tone: 'danger', action: 'stock-out' },
    { label: 'Stock Transfer', icon: 'fa-random', tone: 'info', action: 'stock-transfer' },
    { label: 'Stock Adjustment', icon: 'fa-sliders', tone: 'warning', action: 'stock-adjustment' },
    { label: 'View Transactions', icon: 'fa-list', tone: 'purple', action: 'view-transactions' }
  ];

  recentTransactions: StorageTransaction[] = [];

  isLoading = true;

  private quantityByStore: StoreQuantityPoint[] = [];
  private transactionTrend: TransactionTrendPoint[] = [];
  private stockByStore: StoreStockSegment[] = [];

  private quantityChart: any;
  private trendChart: any;
  private stockChart: any;

  private viewReady = false;

  constructor(
    private storageDashboardRepository: StorageDashboardRepository,
    private toastr: ToastrService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.buildCharts();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  loadDashboard(): void {
    this.isLoading = true;
    console.log('Loading storage dashboard data...');

    this.storageDashboardRepository.GetDashboardData()
      .subscribe({
        next: (response: any) => {
          console.log('API Response:', response);

          if (!response) {
            this.isLoading = false;
            this.toastr.error('No response received from server.');
            return;
          }

          if (response._statusCode !== 200) {
            this.isLoading = false;
            var errorMessage = response._message || 'Unable to load dashboard.';
            this.toastr.error(errorMessage);
            return;
          }

          if (!response._obj) {
            this.isLoading = false;
            this.toastr.error('No dashboard data found.');
            return;
          }

          var data = response._obj;
          console.log('Dashboard Data:', data);

          if (data.Stats) {
            this.stats = {
              TotalStores: data.Stats.TotalStores || 0,
              TotalQuantityIn: data.Stats.TotalQuantityIn || 0,
              TotalQuantityOut: data.Stats.TotalQuantityOut || 0,
              CurrentStock: data.Stats.CurrentStock || 0,
              InTransactions: data.Stats.InTransactions || 0,
              OutTransactions: data.Stats.OutTransactions || 0,
              TotalTransactions: data.Stats.TotalTransactions || 0,
              LowStockStores: data.Stats.LowStockStores || 0
            };
          }

          this.quantityByStore = [];
          if (data.QuantityByStore && data.QuantityByStore.length > 0) {
            for (var i = 0; i < data.QuantityByStore.length; i++) {
              var item = data.QuantityByStore[i];
              this.quantityByStore.push({
                Store: item.Store || '',
                QuantityIn: item.QuantityIn || 0,
                QuantityOut: item.QuantityOut || 0
              });
            }
          }

          this.transactionTrend = [];
          if (data.TransactionTrend && data.TransactionTrend.length > 0) {
            for (var i = 0; i < data.TransactionTrend.length; i++) {
              var item = data.TransactionTrend[i];
              this.transactionTrend.push({
                Label: item.Label || '',
                InCount: item.InCount || 0,
                OutCount: item.OutCount || 0
              });
            }
          }

          this.stockByStore = [];
          if (data.StockByStore && data.StockByStore.length > 0) {
            for (var i = 0; i < data.StockByStore.length; i++) {
              var item = data.StockByStore[i];
              this.stockByStore.push({
                Store: item.Store || '',
                Stock: item.Stock || 0,
                Color: item.Color || '#009688'
              });
            }
          }

          this.recentTransactions = [];
          if (data.RecentTransactions && data.RecentTransactions.length > 0) {
            for (var i = 0; i < data.RecentTransactions.length; i++) {
              var item = data.RecentTransactions[i];
              this.recentTransactions.push({
                ReferenceNo: item.ReferenceNo || '',
                Date: item.Date || '',
                Type: item.Type || '',
                Store: item.Store || '',
                Quantity: item.Quantity || 0,
                Status: item.Status || ''
              });
            }
          }

          console.log('Stats:', this.stats);
          console.log('QuantityByStore:', this.quantityByStore);
          console.log('TransactionTrend:', this.transactionTrend);
          console.log('StockByStore:', this.stockByStore);
          console.log('RecentTransactions:', this.recentTransactions);

          this.isLoading = false;

          setTimeout(function() {
            this.buildCharts();
          }.bind(this), 100);
        },
        error: function(err: any) {
          console.error('Error loading dashboard:', err);
          this.isLoading = false;

          var errorMessage = 'Unable to load storage dashboard.';
          if (err && err.error && err.error._message) {
            errorMessage = err.error._message;
          } else if (err && err.message) {
            errorMessage = err.message;
          }

          this.toastr.error(errorMessage);
        }.bind(this)
      });
  }

  getKpiValue(card: StorageKpiCard): string {
    var value = this.stats[card.valueKey];
    return value + (card.suffix || '');
  }

  statusClass(status: string): string {
    var s = (status || '').toLowerCase();
    if (s === 'posted') {
      return 'status-success';
    }
    if (s === 'pending') {
      return 'status-info';
    }
    if (s === 'cancelled') {
      return 'status-danger';
    }
    return 'status-default';
  }

  typeClass(type: string): string {
    if (type === 'In') {
      return 'type-in';
    }
    return 'type-out';
  }

  onQuickAction(action: StorageQuickAction): void {
    this.actionTriggered.emit(action.action);
    if (action.route) {
      this.router.navigate([action.route]);
    } else {
      this.toastr.info(action.label);
    }
  }

  private buildCharts(): void {
    if (!this.viewReady || this.isLoading) {
      return;
    }

    setTimeout(function() {
      this.buildQuantityChart();
      this.buildTrendChart();
      this.buildStockChart();
    }.bind(this), 0);
  }

  private buildQuantityChart(): void {
    var canvas = document.getElementById('storageQuantityChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('storageQuantityChart canvas not found');
      return;
    }
    if (this.quantityChart) {
      this.quantityChart.destroy();
    }

    var stores = this.quantityByStore.map(function(p) { return p.Store; });
    var quantityIn = this.quantityByStore.map(function(p) { return p.QuantityIn; });
    var quantityOut = this.quantityByStore.map(function(p) { return p.QuantityOut; });

    if (stores.length === 0) {
      this.quantityChart = new Chart(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: ['No Data'],
          datasets: [
            { label: 'Bins In', data: [0], backgroundColor: '#e0e0e0' },
            { label: 'Bins Out', data: [0], backgroundColor: '#e0e0e0' }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' }
        }
      });
      return;
    }

    this.quantityChart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      data: {
        labels: stores,
        datasets: [
          {
            label: 'Quantity In',
            data: quantityIn,
            backgroundColor: '#009688',
            borderColor: '#00796b',
            borderWidth: 1
          },
          {
            label: 'Quantity Out',
            data: quantityOut,
            backgroundColor: '#ef4444',
            borderColor: '#dc2626',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
          yAxes: [{
            ticks: { beginAtZero: true }
          }]
        }
      }
    });
  }

  private buildTrendChart(): void {
    var canvas = document.getElementById('storageTrendChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('storageTrendChart canvas not found');
      return;
    }
    if (this.trendChart) {
      this.trendChart.destroy();
    }

    var labels = this.transactionTrend.map(function(p) { return p.Label; });
    var inCount = this.transactionTrend.map(function(p) { return p.InCount; });
    var outCount = this.transactionTrend.map(function(p) { return p.OutCount; });

    if (labels.length === 0) {
      this.trendChart = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
          labels: ['No Data'],
          datasets: [
            { label: 'In Transactions', data: [0], borderColor: '#e0e0e0', backgroundColor: 'rgba(224,224,224,0.1)' },
            { label: 'Out Transactions', data: [0], borderColor: '#e0e0e0', backgroundColor: 'rgba(224,224,224,0.1)' }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' }
        }
      });
      return;
    }

    this.trendChart = new Chart(canvas.getContext('2d'), {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'In Transactions',
            data: inCount,
            borderColor: '#009688',
            backgroundColor: 'rgba(0, 150, 136, 0.12)',
            fill: true,
            lineTension: 0.3,
            pointBackgroundColor: '#009688'
          },
          {
            label: 'Out Transactions',
            data: outCount,
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.10)',
            fill: true,
            lineTension: 0.3,
            pointBackgroundColor: '#f59e0b'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom' },
        scales: {
          yAxes: [{
            ticks: { beginAtZero: true }
          }]
        }
      }
    });
  }

  private buildStockChart(): void {
    var canvas = document.getElementById('storageStockChart') as HTMLCanvasElement;
    if (!canvas) {
      console.warn('storageStockChart canvas not found');
      return;
    }
    if (this.stockChart) {
      this.stockChart.destroy();
    }

    var stores = this.stockByStore.map(function(s) { return s.Store; });
    var stock = this.stockByStore.map(function(s) { return s.Stock; });
    var colors = this.stockByStore.map(function(s) { return s.Color; });

    if (stores.length === 0 || stock.every(function(v) { return v === 0; })) {
      this.stockChart = new Chart(canvas.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: ['No Data'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0']
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'right' }
        }
      });
      return;
    }

    var finalColors = colors;
    if (finalColors.length === 0) {
      finalColors = ['#009688', '#4f46e5', '#f59e0b'];
    }

    this.stockChart = new Chart(canvas.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: stores,
        datasets: [{
          data: stock,
          backgroundColor: finalColors
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'right' },
        cutoutPercentage: 60
      }
    });
  }

  private destroyCharts(): void {
    var charts = [this.quantityChart, this.trendChart, this.stockChart];
    for (var i = 0; i < charts.length; i++) {
      var chart = charts[i];
      if (chart) {
        chart.destroy();
      }
    }
  }
}